/** global: localStorage */
import {
	CachingBandwidthTest,
	Client,
	Cookie,
	CookieValueRegistry,
	DependencyInjector,
	DownlinkBandwidthTest,
	FocusControl,
	Format,
	HttpClient,
	LanguageLoader,
	Registry,
	type RegistryInterface,
	RequestBandwidthTest,
	RouteGenerator,
	type RouteInfo,
	StaticBandwidthTest,
	StorageDriver,
	Transform,
	TransformDriver, UserAgent, WindowFocus, onDocumentReady,

} from "silver";

import { type MyonLanguageLoader } from "Lang";
import { type MyonEnv, isEnvUserAuthorized } from "shared/Api/MyonEnv";
import { InputMethodDetector } from "shared/InputMethodDetector";
import { DictionarySource } from "shared/Dictionary/DictionarySource";
import { ReducedMotionPreference } from "shared/ReducedMotion/ReducedMotionPreference";
import { OupDictionaryEntryParser } from "shared/Dictionary/OupDictionaryParser";
import { SetLanguageService } from "shared/Services/SetLanguageService";
import { Dictionary } from "shared/Dictionary/Dictionary";
import { BookLaunchDataService } from "shared/Services/BookLaunchDataService";
import { Markdown } from "shared/Markdown";
import { ToastMessageContainer } from "shared/Components/ToastMessage";
import { BookBundleService } from "shared/Services/BookBundleService";
import { ArticleGradeLevelService } from "shared/Services/ArticleGradeLevelService";
import { ArticleSearchService } from "shared/Services/ArticleSearchService";
import { BookBagService } from "shared/Services/BookBagService";
import { Int32ArrayEncoder } from "shared/Int32ArrayEncoder";
import { Dialog } from "shared/Dialog";
import { Modal } from "shared/Modal";
import { cacheFirstResults } from "shared/functions";
import { AuthListener } from "shared/UserAuthorization/AuthListener";

export const dependencyInjector = new DependencyInjector(false);


export const $router = new RouteGenerator();
export const $client = new Client();
export const $httpClient = HttpClient.makeStandard();
export const $route: Readonly<RouteInfo> = $router.routeFromPathInfo($client.url);
export const $format = new Format();
export const $setLanguageService = new SetLanguageService($client, $router);
export const $reducedMotionPreference = ReducedMotionPreference.make();
export const $transform = new Transform(new TransformDriver());
export const $focusControl = new FocusControl(document);
export const $modal = new Modal($focusControl);
export const $windowFocus = new WindowFocus();
export const $cookie = new Cookie(document);
const json = { encode: JSON.stringify, decode: JSON.parse };
export const $userStorage: RegistryInterface<any> = new Registry(new StorageDriver(localStorage, json));
export const $inputMethodDetector = new InputMethodDetector($userStorage);
export const $bookLaunchDataService = new BookLaunchDataService(new StorageDriver(sessionStorage, json));
export const $userAgent = new UserAgent(navigator.userAgent);
export const $dialog = new Dialog($modal);
export const $toastMessageContainer = new ToastMessageContainer();
export const $bookBundleService = new BookBundleService($httpClient);
export const $articleGradeLevelService = new ArticleGradeLevelService($httpClient, $router);
export const $articleSearchService = new ArticleSearchService($httpClient, $router);

export const $markdown = new Markdown({
	a:     "link",
	table: "table -simple -light -markdown"
});


export const $getBandwidthTester = cacheFirstResults((env: MyonEnv): DownlinkBandwidthTest => {
	return new DownlinkBandwidthTest(
		env.bandwidthTest.downlinkThreshold,
		new CachingBandwidthTest(
			new RequestBandwidthTest(
				[
					{ url: `${env.assetCdn}img/speed_test/1.jpg`, size: 9877 },
					{ url: `${env.assetCdn}img/speed_test/2.jpg`, size: 9665 },
					{ url: `${env.assetCdn}img/speed_test/3.jpg`, size: 8671 },
					{ url: `${env.assetCdn}img/speed_test/4.jpg`, size: 9444 },
					{ url: `${env.assetCdn}img/speed_test/5.jpg`, size: 7114 },
					{ url: `${env.assetCdn}img/speed_test/6.jpg`, size: 9714 }
				],
				1000,
				env.bandwidthTest.requestThreshold,
				new StaticBandwidthTest("low")
			),
			new CookieValueRegistry(
				env.bandwidthTest.cacheTTL * 1000,
				"/",
				json,
				window.document
			),
			env.bandwidthTest.cacheCookie
		)
	);
});

export function $makeDictionary(env: MyonEnv): Dictionary|null {
	if (!isEnvUserAuthorized(env) || !env.dictionary) {
		return null;
	}
	const langLoader = $getLanguageLoader(env);
	const dictConfig = env.dictionary;

	const dictionary = new Dictionary(
		new DictionarySource(
			$httpClient,
			function getWordURL(word) {
				return $format.printf(dictConfig.search, [ word ]);
			},
			new OupDictionaryEntryParser(
				(wavFile) => $format.printf(dictConfig.audio, [ wavFile ]),
				dictConfig.lang,
				dictConfig.translate || undefined
			),
			{
				credentials: "include",
				encoding:    HttpClient.ENC_JSON,
				decoding:    HttpClient.ENC_JSON
			}
		),
		undefined
	);
	setTimeout(async () => {
		// load language
		dictionary.setLanguage(await langLoader.load("Widgets\\Dictionary"));
	}, 10); // give a chance to let somebody else saturate it

	return dictionary;
}

export const $getLanguageLoader = cacheFirstResults((env: MyonEnv): MyonLanguageLoader => {
	return LanguageLoader.makeStandard(
		$httpClient,
		function urlBuilder(namespace: string): string {
			return $router.generate("api\\lang", null, {
				lang:   env.language,
				region: env.region,
				c:      env.version,
				nss:    [namespace]
			}, "json");
		}
	);
});

export const $getBookBagService = cacheFirstResults((env: MyonEnv): BookBagService => {
	const key = "bookbag/" + (isEnvUserAuthorized(env) ? env.auth.userId : "anon");
	return new BookBagService(key, $userStorage, new Int32ArrayEncoder());
});

export const $getAuthListener = cacheFirstResults((env: MyonEnv) => {
	return new AuthListener(env, $httpClient, $router, $client, $dialog, $getLanguageLoader(env), $cookie);
});

onDocumentReady(() => {
	document.body.append(
		$modal.element(),
		$toastMessageContainer.element
	);
});

